import type { CSSProperties } from "react";

import type { InsetProperty, OffsetOrigin } from "./types.ts";

export type OffsetOriginConfig = {
  horizontalProperty: "left" | "right" | undefined;
  verticalProperty: "top" | "bottom" | undefined;
  horizontalCenter: boolean;
  verticalCenter: boolean;
};

const offsetOriginConfigMap: Record<OffsetOrigin, OffsetOriginConfig> = {
  topLeft: {
    horizontalProperty: "left",
    verticalProperty: "top",
    horizontalCenter: false,
    verticalCenter: false,
  },
  topCenter: {
    horizontalProperty: undefined,
    verticalProperty: "top",
    horizontalCenter: true,
    verticalCenter: false,
  },
  topRight: {
    horizontalProperty: "right",
    verticalProperty: "top",
    horizontalCenter: false,
    verticalCenter: false,
  },
  centerLeft: {
    horizontalProperty: "left",
    verticalProperty: undefined,
    horizontalCenter: false,
    verticalCenter: true,
  },
  center: {
    horizontalProperty: undefined,
    verticalProperty: undefined,
    horizontalCenter: true,
    verticalCenter: true,
  },
  centerRight: {
    horizontalProperty: "right",
    verticalProperty: undefined,
    horizontalCenter: false,
    verticalCenter: true,
  },
  bottomLeft: {
    horizontalProperty: "left",
    verticalProperty: "bottom",
    horizontalCenter: false,
    verticalCenter: false,
  },
  bottomCenter: {
    horizontalProperty: undefined,
    verticalProperty: "bottom",
    horizontalCenter: true,
    verticalCenter: false,
  },
  bottomRight: {
    horizontalProperty: "right",
    verticalProperty: "bottom",
    horizontalCenter: false,
    verticalCenter: false,
  },
};

export const getOffsetOriginConfig = (offsetOrigin: OffsetOrigin): OffsetOriginConfig =>
  offsetOriginConfigMap[offsetOrigin];

const allInsetProperties: InsetProperty[] = ["top", "right", "bottom", "left"];

export const getEditableInsetProperties = (offsetOrigin: OffsetOrigin): InsetProperty[] => {
  const config = offsetOriginConfigMap[offsetOrigin];
  const properties: InsetProperty[] = [];

  if (config.verticalProperty) properties.push(config.verticalProperty);
  if (config.verticalCenter) {
    properties.push("top", "bottom");
  }
  if (config.horizontalProperty) properties.push(config.horizontalProperty);
  if (config.horizontalCenter) {
    properties.push("left", "right");
  }

  return properties;
};

export const getDisabledInsetProperties = (offsetOrigin: OffsetOrigin): ReadonlySet<InsetProperty> => {
  const editable = new Set(getEditableInsetProperties(offsetOrigin));
  return new Set(allInsetProperties.filter((p) => !editable.has(p)));
};

export const getDefaultInsetValues = (offsetOrigin: OffsetOrigin): Partial<Pick<CSSProperties, InsetProperty>> => {
  const properties = getEditableInsetProperties(offsetOrigin);
  return Object.fromEntries(properties.map((property) => [property, "0px"]));
};

export const clearAllInsetProperties = (): Record<InsetProperty, undefined> => ({
  top: undefined,
  right: undefined,
  bottom: undefined,
  left: undefined,
});

export const clearAllOffsetOriginMargins = (): Pick<
  CSSProperties,
  "marginTop" | "marginRight" | "marginBottom" | "marginLeft"
> => ({
  marginTop: undefined,
  marginRight: undefined,
  marginBottom: undefined,
  marginLeft: undefined,
});

/**
 * Computes the inset CSS properties for an offset origin change, preserving values
 * that remain relevant in the new origin. Resets centered axes to 0px.
 */
export const getInsetValuesForOffsetOriginChange = (
  currentInset: Partial<Pick<CSSProperties, InsetProperty>>,
  newOffsetOrigin: OffsetOrigin,
): Partial<Pick<CSSProperties, InsetProperty>> => {
  const newConfig = offsetOriginConfigMap[newOffsetOrigin];
  const result: Partial<Pick<CSSProperties, InsetProperty>> = {};

  for (const property of allInsetProperties) {
    const isCenteredAxis =
      ((property === "top" || property === "bottom") && newConfig.verticalCenter) ||
      ((property === "left" || property === "right") && newConfig.horizontalCenter);

    if (isCenteredAxis) {
      result[property] = "0px";
    } else if (property === newConfig.verticalProperty || property === newConfig.horizontalProperty) {
      result[property] = currentInset[property] ?? "0px";
    } else {
      result[property] = undefined;
    }
  }

  return result;
};

export type XYFieldMapping = {
  x: InsetProperty;
  y: InsetProperty;
  horizontalCenter: boolean;
  verticalCenter: boolean;
};

/** Maps an offset origin to X/Y field properties. Centered axes use left/top as the primary property. */
export const getXYFieldMapping = (offsetOrigin: OffsetOrigin): XYFieldMapping => {
  const config = offsetOriginConfigMap[offsetOrigin];
  return {
    x: config.horizontalProperty ?? "left",
    y: config.verticalProperty ?? "top",
    horizontalCenter: config.horizontalCenter,
    verticalCenter: config.verticalCenter,
  };
};
